/** Signing in with a mobile number and a code sent by text, then a name for new customers. */
import { errorMessage, patch } from "@taxi/mobile-web/api";
import { BrandArt, CodeStep, PhoneStep } from "@taxi/mobile-web/sign-in";
import { Button, Field } from "@taxi/mobile-web/ui";
import { type FormEvent, useState } from "react";
import brand from "../../../../brand/brand.json";
import logo from "../../../../brand/logo.svg";
import { useSession } from "../lib/session";
import type { CustomerProfile, OtpChallenge } from "../lib/types";

type Step =
  | { kind: "phone" }
  | { kind: "code"; phone: string; challenge: OtpChallenge; sentAt: number }
  | { kind: "name"; me: CustomerProfile };

export function SignInScreen() {
  const { firm, setMe } = useSession();
  const [step, setStep] = useState<Step>({ kind: "phone" });
  return (
    <div className="sign-in">
      {step.kind === "phone" && (
        <PhoneStep
          app="customer"
          hero={
            <>
              <BrandArt firmName={brand.firmName} />
              <h1>Your taxi, booked in a few taps</h1>
              <p>Sign in with your mobile number. We'll text you a code.</p>
            </>
          }
          footer={
            <p className="fine-print">
              By continuing you agree to {firm.name}
              {firm.name.endsWith("s") ? "'" : "'s"}{" "}
              <a href={firm.links.terms} target="_blank" rel="noreferrer">
                terms
              </a>{" "}
              and{" "}
              <a href={firm.links.privacy} target="_blank" rel="noreferrer">
                privacy policy
              </a>
              .
            </p>
          }
          onSent={(phone, challenge) =>
            setStep({ kind: "code", phone, challenge, sentAt: Date.now() })
          }
        />
      )}
      {step.kind === "code" && (
        <CodeStep
          app="customer"
          phone={step.phone}
          challenge={step.challenge}
          sentAt={step.sentAt}
          onBack={() => setStep({ kind: "phone" })}
          onResent={(challenge) => setStep({ ...step, challenge, sentAt: Date.now() })}
          onSignedIn={(session) => {
            const me = session.customer!;
            if (session.isNew || !me.name) setStep({ kind: "name", me });
            else setMe(me);
          }}
        />
      )}
      {step.kind === "name" && <NameStep me={step.me} />}
    </div>
  );
}

function NameStep({ me }: { me: CustomerProfile }) {
  const { setMe } = useSession();
  const [name, setName] = useState(me.name);
  const [email, setEmail] = useState(me.email ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setMe(
        await patch<CustomerProfile>("/customer/me", {
          name: name.trim(),
          ...(email.trim() ? { email: email.trim() } : {}),
        }),
      );
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <form className="sign-in-form" onSubmit={submit}>
      <div className="sign-in-hero">
        <img src={logo} alt={brand.firmName} className="sign-in-logo" />
        <h1>Welcome! What's your name?</h1>
        <p>So your driver knows who they're picking up.</p>
      </div>
      <Field
        label="Your name"
        autoComplete="name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        autoFocus
      />
      <Field
        label="Email (optional)"
        type="email"
        autoComplete="email"
        hint="We'll send your receipts here."
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={error}
      />
      <Button type="submit" loading={busy} disabled={!name.trim()}>
        Start booking
      </Button>
    </form>
  );
}
